// services/v3/datasource/FirestoreSource.ts
// Firestore-backed DataSource for the FUTURE backend schema (SPEC §4.1 + §4.3).
// Full document contract: docs/v3/firestore-schema.md
//
// Read-only except `submitOrder()`, which is the single V3 write
// (a transaction that creates stores/{storeId}/qrcode_orders/{jobId}).
import { FirebaseError } from 'firebase/app'
import {
  doc, getDoc, onSnapshot, runTransaction,
  type DocumentData, type DocumentReference, type Firestore,
} from 'firebase/firestore'
import type {
  DataSource, DataSourceContext, EntityVersions, Unsubscribe, Versioned,
} from '~/types/v3/datasource'
import type {
  ChannelPriceMap, MenuGroup, Product, ProductAllowMap, ProductsPayload,
  SettingRecord, SettingsPayload, SoldoutMap, StoreProfile, TableDoc, TableItem, TableState,
} from '~/types/v3/entities'
import type { QrToken } from '~/types/v3/token'
import type { QrJobCard, SubmitResult } from '~/types/v3/job'
import { getFs, getServerOffset } from '../firebase'
import { readV3Config } from '../config'
import { assertWriteAllowed } from '../writeGuard'

/** Delay before the single re-read when a fetched doc lags behind `meta/versions`. */
const VERSION_RACE_RETRY_MS = 1500
/** Hard cap on the number of product chunks we will read for one store. */
const MAX_CHUNKS = 200

class SessionEndedError extends Error {
  constructor() { super('session ended: qrcode document is gone') }
}
class TableLockedError extends Error {
  constructor() { super('table is locked by the cashier') }
}

type MetaEntity = 'settings' | 'products' | 'channel_price' | 'menuSet'

/* ------------------------------------------------------------------ */
/* narrowing helpers (no `any` in or out)                              */
/* ------------------------------------------------------------------ */

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const asString = (v: unknown): string => (typeof v === 'string' ? v : '')

const asNumber = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  if (typeof v === 'string') {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

/** A `version` field is always written by the backend; '' means "unknown". */
const versionOf = (d: Record<string, unknown> | undefined): string => asString(d?.version)

/** Firestore arrays can round-trip as maps when written by some tooling. */
const toArray = (v: unknown): unknown[] => {
  if (Array.isArray(v)) return v
  if (isRecord(v)) {
    return Object.keys(v)
      .sort((a, b) => {
        const na = Number(a); const nb = Number(b)
        return Number.isFinite(na) && Number.isFinite(nb) ? na - nb : a.localeCompare(b)
      })
      .map((k) => v[k])
  }
  return []
}

const byRow = <T extends { row?: number }>(list: T[]): T[] =>
  list.every((x) => typeof x?.row === 'number')
    ? [...list].sort((a, b) => (a.row as number) - (b.row as number))
    : list

/* ------------------------------------------------------------------ */

export class FirestoreSource implements DataSource {
  readonly kind = 'firestore' as const
  /** The Firestore schema always ships `meta/versions`. */
  readonly supportsVersions = true

  private ctx: DataSourceContext | null = null
  private db: Firestore | null = null
  private readonly subs = new Set<Unsubscribe>()
  /** Latest versions seen on `meta/versions`; used for the race check. */
  private lastMetaVersion: Partial<Record<MetaEntity, string>> = {}

  /* ---------------- lifecycle ---------------- */

  async init(ctx: DataSourceContext): Promise<void> {
    this.ctx = ctx
    this.db = getFs()
    // Prime the version map so the very first fetch can detect a stale read.
    try {
      const v = await this.getVersions()
      if (v) this.lastMetaVersion = { ...v }
    } catch (e) {
      console.warn('[v3] firestore: meta/versions unavailable at init', e)
    }
  }

  dispose(): void {
    for (const un of this.subs) {
      try { un() } catch (e) { console.warn('[v3] firestore: unsubscribe failed', e) }
    }
    this.subs.clear()
    this.lastMetaVersion = {}
    this.ctx = null
    this.db = null
  }

  /* ---------------- refs ---------------- */

  private get storeId(): string {
    const id = this.ctx?.storeId
    if (!id) throw new Error('[v3] FirestoreSource used before init()')
    return id
  }

  private get fs(): Firestore {
    if (!this.db) this.db = getFs()
    return this.db
  }

  private storeDoc(...segments: string[]): DocumentReference<DocumentData> {
    return doc(this.fs, 'stores', this.storeId, ...segments)
  }

  private qrcodeDoc(qrcodeId: string): DocumentReference<DocumentData> {
    return doc(this.fs, 'qrcodes', qrcodeId)
  }

  /** Registers `un` so `dispose()` can tear it down; returned fn is idempotent. */
  private track(un: Unsubscribe): Unsubscribe {
    this.subs.add(un)
    return () => {
      if (!this.subs.has(un)) return
      this.subs.delete(un)
      try { un() } catch (e) { console.warn('[v3] firestore: unsubscribe failed', e) }
    }
  }

  private async readDoc(ref: DocumentReference<DocumentData>): Promise<Record<string, unknown> | undefined> {
    const snap = await getDoc(ref)
    return snap.exists() ? (snap.data() as Record<string, unknown>) : undefined
  }

  /* ---------------- versions ---------------- */

  async getVersions(): Promise<EntityVersions | null> {
    const d = await this.readDoc(this.storeDoc('meta', 'versions'))
    if (!d) return null
    const v = mapVersions(d)
    this.lastMetaVersion = { ...v }
    return v
  }

  watchVersions(cb: (v: EntityVersions) => void): Unsubscribe {
    const un = onSnapshot(
      this.storeDoc('meta', 'versions'),
      (snap) => {
        if (!snap.exists()) return
        const v = mapVersions(snap.data() as Record<string, unknown>)
        this.lastMetaVersion = { ...v }
        cb(v)
      },
      (e) => console.warn('[v3] firestore: watchVersions error', e),
    )
    return this.track(un)
  }

  /**
   * Reads an entity and, when the document's own `version` disagrees with the
   * version advertised by `meta/versions` (backend batch not fully visible yet),
   * retries ONCE after 1.5 s and then accepts whatever comes back (SPEC §4.3).
   */
  private async withVersionRace<T>(
    entity: MetaEntity,
    load: () => Promise<Versioned<T>>,
  ): Promise<Versioned<T>> {
    const first = await load()
    const expected = this.lastMetaVersion[entity]
    if (!expected || first.version === expected) return first
    await new Promise<void>((resolve) => { setTimeout(resolve, VERSION_RACE_RETRY_MS) })
    try {
      return await load()
    } catch (e) {
      console.warn('[v3] firestore: version race retry failed, keeping first read', e)
      return first
    }
  }

  /* ---------------- entity fetches ---------------- */

  /** `stores/{storeId}/public/settings` -> `{ version, values: { settingId: value } }` */
  fetchSettings(): Promise<Versioned<SettingsPayload>> {
    return this.withVersionRace('settings', async () => {
      const d = await this.readDoc(this.storeDoc('public', 'settings'))
      const values = isRecord(d?.values) ? (d?.values as Record<string, unknown>) : {}
      const list: SettingRecord[] = Object.keys(values).map((settingId) => ({
        settingId,
        value: values[settingId],
      }))
      return { version: versionOf(d), data: { list } }
    })
  }

  /** `stores/{storeId}/public/channel_price` -> `{ version, channels: { ch: { pid: price } } }` */
  fetchChannelPrice(): Promise<Versioned<ChannelPriceMap>> {
    return this.withVersionRace('channel_price', async () => {
      const d = await this.readDoc(this.storeDoc('public', 'channel_price'))
      const channels = isRecord(d?.channels) ? (d?.channels as Record<string, unknown>) : {}
      const map: ChannelPriceMap = {}
      for (const channelId of Object.keys(channels)) {
        const prices = channels[channelId]
        if (!isRecord(prices)) continue
        const out: Record<string, number> = {}
        for (const productId of Object.keys(prices)) out[productId] = asNumber(prices[productId])
        map[channelId] = out
      }
      return { version: versionOf(d), data: map }
    })
  }

  /** `stores/{storeId}/menuSets/{settingId}` -> `{ version, products: { productId: true } }` */
  fetchMenuSet(settingId: string): Promise<Versioned<ProductAllowMap>> {
    return this.withVersionRace('menuSet', async () => {
      if (!settingId) return { version: '', data: {} as ProductAllowMap }
      const d = await this.readDoc(this.storeDoc('menuSets', settingId))
      const products = isRecord(d?.products) ? (d?.products as Record<string, unknown>) : {}
      const allow: ProductAllowMap = {}
      for (const productId of Object.keys(products)) {
        if (products[productId]) allow[productId] = true
      }
      return { version: versionOf(d), data: allow }
    })
  }

  /**
   * `products/index` `{ version, chunkCount, groups:[{productId,name,row,itemIds}] }`
   * plus `products/chunk_{n}` `{ version, items }`.
   * `list` = chunks concatenated in order; `computed` = groups rebuilt from `itemIds`.
   */
  fetchProducts(): Promise<Versioned<ProductsPayload>> {
    return this.withVersionRace('products', async () => {
      const index = await this.readDoc(this.storeDoc('products', 'index'))
      if (!index) return { version: '', data: { list: [], computed: [] } }

      const chunkCount = Math.max(0, Math.min(MAX_CHUNKS, Math.trunc(asNumber(index.chunkCount))))
      const chunks = await Promise.all(
        Array.from({ length: chunkCount }, (_unused, n) => this.readDoc(this.storeDoc('products', `chunk_${n}`))),
      )

      const list: Product[] = []
      for (const chunk of chunks) {
        for (const raw of toArray(chunk?.items)) {
          if (isRecord(raw) && asString(raw.productId)) list.push(raw as unknown as Product)
        }
      }

      const byId = new Map<string, Product>()
      for (const p of list) byId.set(p.productId, p)

      const groups: MenuGroup[] = []
      for (const raw of toArray(index.groups)) {
        if (!isRecord(raw)) continue
        const productId = asString(raw.productId)
        if (!productId) continue
        const itemIds = toArray(raw.itemIds).map(asString).filter(Boolean)
        const items = byRow(
          itemIds
            .map((id) => byId.get(id))
            .filter((p): p is Product => p !== undefined),
        )
        const group: MenuGroup = { productId, name: asString(raw.name), items }
        if (typeof raw.row === 'number') group.row = raw.row
        groups.push(group)
      }

      return { version: versionOf(index), data: { list, computed: byRow(groups) } }
    })
  }

  /** Same contract as RtdbSource: HTTP profile lookup, `null` on any failure. */
  async getStoreProfile(): Promise<StoreProfile | null> {
    const storeId = this.storeId
    let base = ''
    try { base = readV3Config().storeApiBase } catch { base = '' }
    if (!base) return null
    try {
      const res = await fetch(`${base.replace(/\/+$/, '')}/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${storeId}` },
      })
      if (!res.ok) return null
      const body: unknown = await res.json()
      const name = isRecord(body) ? asString(body.name) : ''
      return name ? { name } : null
    } catch (e) {
      console.warn('[v3] firestore: getStoreProfile failed', e)
      return null
    }
  }

  /* ---------------- realtime ---------------- */

  watchSoldout(cb: (m: SoldoutMap) => void): Unsubscribe {
    const un = onSnapshot(
      this.storeDoc('appState', 'soldout'),
      (snap) => cb(snap.exists() ? (snap.data() as SoldoutMap) : {}),
      (e) => console.warn('[v3] firestore: watchSoldout error', e),
    )
    return this.track(un)
  }

  watchTableState(table: string, cb: (s: TableState | null) => void): Unsubscribe {
    const un = onSnapshot(
      this.storeDoc('tableState', table),
      (snap) => cb(snap.exists() ? (snap.data() as TableState) : null),
      (e) => console.warn('[v3] firestore: watchTableState error', e),
    )
    return this.track(un)
  }

  watchTableItems(table: string, cb: (d: TableDoc | null) => void): Unsubscribe {
    const un = onSnapshot(
      this.storeDoc('tables', table),
      (snap) => cb(snap.exists() ? toTableDoc(snap.data() as Record<string, unknown>) : null),
      (e) => console.warn('[v3] firestore: watchTableItems error', e),
    )
    return this.track(un)
  }

  /** `null` => the qrcode doc was deleted => the table was paid / the QR revoked. */
  watchQrcode(qrcodeId: string, cb: (t: QrToken | null) => void): Unsubscribe {
    const un = onSnapshot(
      this.qrcodeDoc(qrcodeId),
      (snap) => cb(snap.exists() ? toToken(qrcodeId, snap.data() as Record<string, unknown>) : null),
      (e) => console.warn('[v3] firestore: watchQrcode error', e),
    )
    return this.track(un)
  }

  async getQrcode(qrcodeId: string): Promise<QrToken | null> {
    const snap = await getDoc(this.qrcodeDoc(qrcodeId))
    return snap.exists() ? toToken(qrcodeId, snap.data() as Record<string, unknown>) : null
  }

  async getTableState(table: string): Promise<TableState | null> {
    const d = await this.readDoc(this.storeDoc('tableState', table))
    return d ? (d as TableState) : null
  }

  async getServerNowISO(): Promise<string> {
    const offset = await getServerOffset()
    return new Date(Date.now() + offset).toISOString()
  }

  /* ---------------- the one write ---------------- */

  async submitOrder(job: QrJobCard): Promise<SubmitResult> {
    assertWriteAllowed('qrcode_orders')

    // Cheap pre-check so a locked table never even opens a transaction.
    try {
      const pre = await this.getTableState(job.table)
      if (pre?.lock === true) return { ok: false, jobId: job.jobId, reason: 'locked' }
    } catch (e) {
      console.warn('[v3] firestore: table lock pre-check failed', e)
    }

    const orderRef = this.storeDoc('qrcode_orders', job.jobId)
    const qrcodeRef = this.qrcodeDoc(job.qrcodeId)
    const tableStateRef = this.storeDoc('tableState', job.table)

    try {
      const outcome = await runTransaction(this.fs, async (tx) => {
        const q = await tx.get(qrcodeRef)
        if (!q.exists()) throw new SessionEndedError()

        const ts = await tx.get(tableStateRef)
        if (ts.exists() && (ts.data() as TableState).lock === true) throw new TableLockedError()

        const o = await tx.get(orderRef)
        if (o.exists()) return 'exists' as const

        tx.set(orderRef, job)
        return 'created' as const
      })
      return { ok: true, jobId: job.jobId, simulated: false, alreadyExisted: outcome === 'exists' }
    } catch (error) {
      return { ok: false, jobId: job.jobId, reason: submitFailureReason(error), error }
    }
  }
}

/* ------------------------------------------------------------------ */
/* pure mappers                                                        */
/* ------------------------------------------------------------------ */

/** `meta/versions` `{settings, products, channel_price, menuSets, updatedAt}`. */
function mapVersions(d: Record<string, unknown>): EntityVersions {
  return {
    settings: asString(d.settings),
    products: asString(d.products),
    channel_price: asString(d.channel_price),
    menuSet: asString(d.menuSets),
  }
}

function toTableDoc(d: Record<string, unknown>): TableDoc {
  const items = toArray(d.items).filter(isRecord) as unknown as TableItem[]
  return { items }
}

function toToken(qrcodeId: string, d: Record<string, unknown>): QrToken {
  return { ...(d as unknown as QrToken), qrcodeId: asString(d.qrcodeId) || qrcodeId }
}

const NETWORK_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'resource-exhausted',
  'auth/network-request-failed',
])

function submitFailureReason(error: unknown): 'locked' | 'session_ended' | 'network' | 'unknown' {
  if (error instanceof SessionEndedError) return 'session_ended'
  if (error instanceof TableLockedError) return 'locked'
  if (error instanceof FirebaseError && NETWORK_CODES.has(error.code)) return 'network'
  const message = error instanceof Error ? error.message.toLowerCase() : ''
  if (message.includes('network') || message.includes('offline') || message.includes('timeout')) return 'network'
  return 'unknown'
}
